import { toast } from 'sonner';
import type { UseFormReturn } from 'react-hook-form';
import { registerActionHandler } from '../action-registry';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';
import { applyResponseMapping } from '../response-mapping';
import { SYNTHETIC_POWERBI_RESOLVED_LABEL } from '../synthetic-form-keys';
import {
  fetchResolveContentName,
  type ResolvePowerBIContentNameResult,
} from '../resolve-content-name-rest';
import {
  usePowerBIBrowseDialogStore,
  type PowerBISelection,
} from '../../powerbi-browse/stores/powerbi-browse-dialog-store';

type ManifestForm = UseFormReturn<Record<string, unknown>>;

/**
 * `openPowerBIBrowseModal` — the Power BI content picker's single action handler
 * (build plan step 14, decision-of-record #3: one handler branching on `ctx.trigger`,
 * since the engine allows exactly one `action` per field and forwards `trigger`).
 *
 * - `trigger: 'manual'` (folder-icon click): opens `PowerBIBrowseDialog`; on a pick,
 *   writes `targetURL` and runs the same name-resolution side effects as a successful
 *   blur-resolve — synthesized directly from the picked row, since the modal's own
 *   `/content-browse` call already returned the name (R2's own cost accounting: "one
 *   call to open the modal, two per workspace visited" — no extra round trip here).
 * - `trigger: 'blur'` (typed/pasted URL): best-effort resolves via `/resolve-content-name`.
 *   R4 is absolute — never blocks, never toasts, swallows every failure. The REST client
 *   itself already collapses every failure mode to `{resolved:false}` and never throws,
 *   so this branch has nothing left to catch.
 */

/** R3/R4 side effects for a resolved name — shared by both trigger branches so a
 *  modal pick and a blur-resolve behave identically once a name is known: write the
 *  helper text (`_powerBIResolvedLabel`, read by StringField via `resolvedLabelKey`),
 *  pre-fill Name via `onlyUnset` (never overwrites a user-set Name), then re-trigger
 *  `originalFileName` validation ONLY when the write actually landed — closes the
 *  stale-`isValid` gap left by a required field going empty → populated without RHF
 *  re-validating it. */
function applyResolvedName(form: ManifestForm, result: ResolvePowerBIContentNameResult): void {
  if (!result.resolved) {
    form.setValue(SYNTHETIC_POWERBI_RESOLVED_LABEL, '', { shouldDirty: false });
    return;
  }
  const label = result.workspaceName ? `${result.workspaceName} / ${result.name}` : result.name;
  form.setValue(SYNTHETIC_POWERBI_RESOLVED_LABEL, label, { shouldDirty: false });
  applyResponseMapping(form, { originalFileName: '$response.name' }, { name: result.name }, true);
  if (form.getValues('originalFileName') === result.name) {
    form.trigger('originalFileName');
  }
}

function credentialArgs(values: Record<string, unknown>): {
  encryptedAccessToken?: string;
  integrationKeyId?: string;
} {
  return {
    encryptedAccessToken:
      typeof values.encryptedAccessToken === 'string' ? values.encryptedAccessToken : undefined,
    integrationKeyId:
      typeof values.integrationKeyId === 'string' ? values.integrationKeyId : undefined,
  };
}

registerActionHandler('openPowerBIBrowseModal', async (ctx) => {
  const { form, values, trigger, appMeta, t, apolloClient } = ctx;

  if (trigger === 'blur') {
    const targetURL = typeof values.targetURL === 'string' ? values.targetURL.trim() : '';
    if (!targetURL) return;
    const apiUrl = appMeta?.apiUrl;
    // No apiUrl to call — R4 says silence, not an error, so just no-op.
    if (!apiUrl) return;
    const token = await getSmallAppRestToken(apolloClient);
    const result = await fetchResolveContentName({
      apiUrl,
      token,
      targetURL,
      ...credentialArgs(values),
    });
    applyResolvedName(form, result);
    return;
  }

  // trigger === 'manual' — the folder-icon click opens the browse dialog.
  const apiUrl = appMeta?.apiUrl;
  if (!apiUrl) {
    toast.error(t('This app does not expose a content browsing API'));
    return;
  }

  const selection = await new Promise<PowerBISelection | null>((resolve) => {
    usePowerBIBrowseDialogStore.getState().open({
      ...credentialArgs(values),
      apiUrl,
      resolve,
    });
  });
  if (!selection) return;

  form.setValue('targetURL', selection.webUrl, { shouldDirty: true, shouldValidate: true });
  applyResolvedName(form, {
    resolved: true,
    name: selection.name,
    type: selection.type,
    // From the modal we KNOW the workspace — the user navigated into it. Passing it through
    // makes a pick read "Workspace / Report", matching the pasted-URL path instead of showing
    // strictly less information than it.
    workspaceName: selection.workspaceName ?? null,
  });
});
